import { useState, useEffect } from "react";
import Swipeable from "@/components/Swipeable";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Copy, MessageCircle, FileText, Image as ImageIcon,
  Eye, Download, Trash2, ChevronDown, Inbox, Clock, CheckCircle2, Phone, Mail, FolderUp, Repeat,
  X, Paperclip, ClipboardCheck, RotateCcw
} from "lucide-react";
import { authGet, authPut, authDelete, getAuthToken, isAdvisor } from "../utils/auth";
import MobileBottomNav from "../components/MobileBottomNav";
import MobileSidebar from "../components/MobileSidebar";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  new: { label: "New", color: "text-[#A87608]", bg: "bg-[#D4A017]/15" },
  in_process: { label: "In Process", color: "text-sky-600", bg: "bg-sky-500/15" },
  completed: { label: "Completed", color: "text-emerald-600", bg: "bg-emerald-500/15" },
};

const TABS = [
  { id: "all", label: "All", icon: Inbox, color: "#44506B" },
  { id: "new", label: "New", icon: Inbox, color: "#A87608" },
  { id: "in_process", label: "In Process", icon: Clock, color: "#0369a1" },
  { id: "completed", label: "Completed", icon: CheckCircle2, color: "#047857" },
];

const fmtSize = (b) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");

// Builds a professional health-insurance "login" email (subject + body) from a submission.
const buildLoginEmail = (s, quoteName) => {
  const members = s.members || [];
  const insured = members.filter((m) => m.is_insured !== false);
  const listMembers = insured.length ? insured : members;
  const count = listMembers.length;
  const subject = `New Health Insurance Proposal — Login Request — ${s.name || "Client"}${count ? ` (${count} member${count > 1 ? "s" : ""})` : ""}`;

  const L = [];
  L.push("Dear Team,");
  L.push("");
  L.push("Please find below the details and documents for logging in a new health insurance proposal. Kindly proceed with the proposal and revert for any further requirement.");
  L.push("");
  L.push("── PROPOSER ──");
  L.push(`Name: ${s.name || "—"}`);
  if (s.mobile) L.push(`Mobile: ${s.mobile}`);
  if (s.email) L.push(`Email: ${s.email}`);
  L.push(`Case type: ${s.is_portability ? "Portability" : "Fresh Policy"}`);

  if (quoteName) {
    L.push("");
    L.push("── PLAN / QUOTE ──");
    L.push(`Quote enclosed: ${quoteName}`);
  }

  L.push("");
  L.push("── INSURED MEMBERS ──");
  listMembers.forEach((m, i) => {
    L.push(`${i + 1}. ${m.name || `Member ${i + 1}`}${m.relationship ? ` (${m.relationship})` : ""}${m.is_proposer ? " — Proposer" : ""}`);
    const bits = [];
    if (m.dob) bits.push(`DOB: ${m.dob}`);
    if (m.height) bits.push(`Height: ${m.height} cm`);
    if (m.weight) bits.push(`Weight: ${m.weight} kg`);
    if (bits.length) L.push(`   ${bits.join("  |  ")}`);
    if (m.medical_history) L.push(`   Medical history: ${m.medical_history}`);
    if (m.hospitalization_reason || m.hospitalization_when) L.push(`   Past hospitalization: ${m.hospitalization_reason || "—"}${m.hospitalization_when ? ` (approx ${m.hospitalization_when})` : ""}`);
    if (m.medication_details) L.push(`   Current medication: ${m.medication_details}`);
  });
  if (!listMembers.some((m) => m.medical_history) && s.medical_history) L.push(`   Medical history (all): ${s.medical_history}`);

  if (s.nominee_name || s.nominee_relationship || s.nominee_dob) {
    L.push("");
    L.push("── NOMINEE ──");
    L.push(`${s.nominee_name || "—"}${s.nominee_relationship ? ` (${s.nominee_relationship})` : ""}${s.nominee_dob ? ` — DOB: ${s.nominee_dob}` : ""}`);
  }

  L.push("");
  L.push("── DOCUMENTS ENCLOSED ──");
  const memberFileIds = new Set(members.flatMap((m) => (m.files || []).map((f) => f.id)));
  members.forEach((m) => {
    const fs = m.files || [];
    if (fs.length) L.push(`- ${m.name || m.relationship || "Member"}: ${fs.map((f) => f.label || f.name).join(", ")}`);
  });
  const portFiles = (s.files || []).filter((f) => f.member_key === "portability");
  if (portFiles.length) L.push(`- Previous policies (portability): ${portFiles.map((f) => f.label || f.name).join(", ")}`);
  const others = (s.files || []).filter((f) => !memberFileIds.has(f.id) && f.member_key !== "portability");
  if (others.length) L.push(`- Other documents: ${others.map((f) => f.label || f.name).join(", ")}`);
  if (quoteName) L.push(`- Quote: ${quoteName}`);

  L.push("");
  L.push("Regards,");
  L.push("Prashant K. Unadkat");
  L.push("Kavach Box — Shree Finserv Group");

  return { subject, body: L.join("\n") };
};

export default function DocSubmissionsAdmin() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subs, setSubs] = useState([]);
  const [counts, setCounts] = useState({});
  const [tab, setTab] = useState("all");
  const [expandedId, setExpandedId] = useState(null);
  const [busyFile, setBusyFile] = useState("");
  const [showSidebar, setShowSidebar] = useState(false);
  const [draftSub, setDraftSub] = useState(null);
  const [draftSubject, setDraftSubject] = useState("");
  const [draftBody, setDraftBody] = useState("");
  const [quoteFile, setQuoteFile] = useState(null);
  const [dlAll, setDlAll] = useState(false);

  const shareLink = `${window.location.origin}/upload-documents`;

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const res = await authGet("/api/advisor/doc-submissions");
      if (res.ok) {
        setSubs(res.data?.submissions || []);
        setCounts(res.data?.counts || {});
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(shareLink);
    toast.success("Link copied — share it with your client");
  };

  const shareWhatsApp = () => {
    const msg = `Hello! To proceed with your health insurance policy, please upload your documents (Aadhaar, PAN, cancelled cheque, insured members' details) using this secure link:\n${shareLink}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const updateStatus = async (id, status) => {
    try {
      const res = await authPut(`/api/advisor/doc-submissions/${id}`, { status });
      if (res.ok) {
        setSubs((prev) => prev.map((s) => (s.id === id ? { ...s, status } : s)));
        fetchData();
      } else toast.error(res.data?.detail || "Failed to update");
    } catch { toast.error("Network error"); }
  };

  const deleteSub = async (id) => {
    if (!window.confirm("Delete this submission record? Uploaded files will no longer be listed.")) return;
    try {
      const res = await authDelete(`/api/advisor/doc-submissions/${id}`);
      if (res.ok) { toast.success("Submission deleted"); fetchData(); }
      else toast.error(res.data?.detail || "Failed to delete");
    } catch { toast.error("Network error"); }
  };

  const fetchFileBlob = async (subId, f) => {
    const token = getAuthToken();
    const res = await fetch(`${API_URL}/api/advisor/doc-submissions/${subId}/files/${f.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error("fetch failed");
    return await res.blob();
  };

  const viewFile = async (subId, f) => {
    setBusyFile(f.id);
    try {
      const blob = await fetchFileBlob(subId, f);
      window.open(URL.createObjectURL(blob), "_blank");
    } catch { toast.error("Could not open file"); }
    setBusyFile("");
  };

  const downloadFile = async (subId, f) => {
    setBusyFile(f.id);
    try {
      const blob = await fetchFileBlob(subId, f);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = f.name || "document";
      a.click();
    } catch { toast.error("Could not download file"); }
    setBusyFile("");
  };

  const visible = tab === "all" ? subs : subs.filter((s) => s.status === tab);

  // ---- Login email draft ----
  const openEmailDraft = (s) => {
    const { subject, body } = buildLoginEmail(s, null);
    setDraftSub(s);
    setDraftSubject(subject);
    setDraftBody(body);
    setQuoteFile(null);
  };
  const regenerateDraft = () => {
    if (!draftSub) return;
    const { subject, body } = buildLoginEmail(draftSub, quoteFile?.name || null);
    setDraftSubject(subject);
    setDraftBody(body);
    toast.success("Draft regenerated from client data");
  };
  const onQuoteUpload = (file) => {
    if (!file) return;
    setQuoteFile(file);
    const { subject, body } = buildLoginEmail(draftSub, file.name);
    setDraftSubject(subject);
    setDraftBody(body);
    toast.success("Quote added to the draft");
  };
  const copyText = (text, what) => {
    navigator.clipboard.writeText(text);
    toast.success(`${what} copied`);
  };
  const openInMailApp = () => {
    window.location.href = `mailto:?subject=${encodeURIComponent(draftSubject)}&body=${encodeURIComponent(draftBody)}`;
  };
  const downloadAllAttachments = async () => {
    if (!draftSub) return;
    setDlAll(true);
    try {
      for (const f of draftSub.files || []) {
        const blob = await fetchFileBlob(draftSub.id, f);
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = f.name || "document";
        a.click();
        await new Promise((r) => setTimeout(r, 400));
      }
      if (quoteFile) {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(quoteFile);
        a.download = quoteFile.name;
        a.click();
      }
      toast.success("Downloading all attachments — attach them in your email");
    } catch {
      toast.error("Some files could not be downloaded");
    }
    setDlAll(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-[#A87608] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pb-24">
      <header className="bg-white/80 backdrop-blur-xl sticky top-0 z-40 border-b border-[#C9D4E6]">
        <div className="px-4 py-4 flex items-center gap-3 max-w-4xl mx-auto">
          <button onClick={() => navigate(-1)} className="text-[#3A4557] hover:text-[#1B2A4A]" data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-lg font-bold text-[#1B2A4A] flex items-center gap-2">
              <FolderUp className="h-5 w-5 text-[#A87608]" /> Client Documents
            </h1>
            <p className="text-[#3A4557] text-xs">Policy-login documents uploaded by clients</p>
          </div>
        </div>
      </header>

      <main className="px-4 py-5 max-w-4xl mx-auto space-y-5">
        {/* Share link */}
        <div className="glass-card p-5" data-testid="doc-share-card">
          <h2 className="text-[#1B2A4A] font-semibold text-sm mb-1">Share the upload link with your client</h2>
          <p className="text-[#3A4557] text-xs mb-3">Client fills the form, attaches PDF/JPG/PNG documents, and everything lands here instantly.</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="flex-1 bg-[#EDF1F8]/50 border border-[#C9D4E6] rounded-xl px-3 py-2.5 overflow-hidden">
              <span className="text-sm text-[#2E3B57] truncate block" data-testid="doc-share-link">{shareLink}</span>
            </div>
            <div className="flex gap-2">
              <button onClick={copyLink} data-testid="doc-copy-link-btn" className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#EDF1F8] hover:bg-[#E2E9F4] text-[#1B2A4A] text-sm font-medium">
                <Copy className="h-4 w-4" /> Copy
              </button>
              <button onClick={shareWhatsApp} data-testid="doc-share-whatsapp-btn" className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-green-600 hover:bg-green-700 text-white text-sm font-medium">
                <MessageCircle className="h-4 w-4" /> WhatsApp
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Swipeable className="flex gap-2 pb-1">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            const count = t.id === "all" ? counts.total : counts[t.id];
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`subs-tab-${t.id}`}
                className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors border"
                style={{
                  backgroundColor: active ? t.color : "transparent",
                  color: active ? "#ffffff" : t.color,
                  borderColor: active ? t.color : "#C9D4E6",
                }}
              >
                <Icon className="h-4 w-4" />
                {t.label}
                <span className={`px-1.5 rounded-full text-xs ${active ? "bg-black/15" : "bg-[#EDF1F8]"}`}>{count ?? 0}</span>
              </button>
            );
          })}
        </Swipeable>

        {/* Submissions */}
        {visible.length === 0 ? (
          <div className="text-center py-14 text-[#3A4557]" data-testid="no-submissions">
            <Inbox className="h-10 w-10 mx-auto mb-3 opacity-50" />
            <p className="text-sm">No submissions yet. Share the link above with your clients.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {visible.map((s) => {
              const meta = STATUS_META[s.status] || STATUS_META.new;
              const expanded = expandedId === s.id;
              const renderFile = (f) => (
                <div key={f.id} className="flex items-center gap-2 bg-[#EDF1F8]/40 border border-[#C9D4E6] rounded-xl px-3 py-2" data-testid={`file-row-${f.id}`}>
                  {f.content_type === "application/pdf" ? <FileText className="h-4 w-4 text-red-600 flex-shrink-0" /> : <ImageIcon className="h-4 w-4 text-sky-600 flex-shrink-0" />}
                  <div className="min-w-0 flex-1">
                    {f.label && <span className="inline-block text-[9px] px-1.5 py-0.5 rounded-full bg-[#D4A017]/15 text-[#A87608] font-semibold mb-0.5">{f.label}</span>}
                    <p className="text-[#1B2A4A] text-xs truncate">{f.name}</p>
                    <p className="text-[#3A4557] text-[10px]">{fmtSize(f.size || 0)}</p>
                  </div>
                  <button onClick={() => viewFile(s.id, f)} disabled={busyFile === f.id}
                    className="p-1.5 rounded-lg hover:bg-[#E2E9F4] text-[#2E3B57]" title="View" data-testid={`view-file-${f.id}`}>
                    {busyFile === f.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
                  </button>
                  <button onClick={() => downloadFile(s.id, f)} disabled={busyFile === f.id}
                    className="p-1.5 rounded-lg hover:bg-[#E2E9F4] text-[#2E3B57]" title="Download" data-testid={`download-file-${f.id}`}>
                    <Download className="h-4 w-4" />
                  </button>
                </div>
              );
              return (
                <div key={s.id} className="glass-card overflow-hidden" data-testid={`submission-card-${s.id}`}>
                  <button
                    type="button"
                    onClick={() => setExpandedId(expanded ? null : s.id)}
                    className="w-full flex items-center gap-3 p-4 text-left"
                    data-testid={`submission-toggle-${s.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[#1B2A4A] font-semibold text-sm">{s.name}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${meta.bg} ${meta.color}`}>{meta.label}</span>
                        {s.is_portability && <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-emerald-500/15 text-emerald-700 flex items-center gap-1"><Repeat className="h-3 w-3" /> Portability</span>}
                        <span className="text-xs px-2 py-0.5 rounded-full bg-[#EDF1F8] text-[#2E3B57]">{(s.files || []).length} files</span>
                      </div>
                      <p className="text-[#3A4557] text-xs mt-1">{s.mobile}{s.email ? ` · ${s.email}` : ""} · {fmtDate(s.created_at)}</p>
                    </div>
                    <ChevronDown className={`h-4 w-4 text-[#3A4557] transition-transform flex-shrink-0 ${expanded ? "rotate-180" : ""}`} />
                  </button>

                  {expanded && (
                    <div className="border-t border-[#C9D4E6] p-4 space-y-4">
                      {/* Contact + nominee */}
                      <div className="grid sm:grid-cols-2 gap-4 text-xs">
                        <div className="space-y-1.5">
                          <p className="text-[#A87608] font-semibold">Contact</p>
                          <p className="text-[#2E3B57] flex items-center gap-1.5"><Phone className="h-3 w-3" /> <a href={`tel:${s.mobile}`} className="hover:underline">{s.mobile}</a></p>
                          {s.email && <p className="text-[#2E3B57] flex items-center gap-1.5"><Mail className="h-3 w-3" /> {s.email}</p>}
                        </div>
                        {(s.nominee_name || s.nominee_dob || s.nominee_relationship) && (
                          <div className="space-y-1.5">
                            <p className="text-[#A87608] font-semibold">Nominee</p>
                            <p className="text-[#2E3B57]">{s.nominee_name || "—"}{s.nominee_relationship ? ` (${s.nominee_relationship})` : ""}{s.nominee_dob ? ` · DOB: ${s.nominee_dob}` : ""}</p>
                          </div>
                        )}
                      </div>

                      {/* Members with their documents */}
                      {(s.members || []).length > 0 && (
                        <div className="space-y-3">
                          {s.members.map((m, i) => (
                            <div key={m.key || i} className="border border-[#C9D4E6] rounded-xl p-3 bg-[#EDF1F8]/30" data-testid={`admin-member-${i}`}>
                              <div className="flex items-center gap-2 flex-wrap mb-1">
                                <span className="text-[#1B2A4A] font-semibold text-sm">{m.name || `Member ${i + 1}`}</span>
                                {m.relationship && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#EDF1F8] text-[#2E3B57]">{m.relationship}</span>}
                                {m.is_proposer && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#D4A017]/20 text-[#A87608] font-semibold">Proposer</span>}
                                {m.is_insured
                                  ? <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-700">Insured</span>
                                  : <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#EDF1F8] text-[#3A4557]">Payer only</span>}
                              </div>
                              {(m.height || m.weight) && (
                                <p className="text-[#3A4557] text-[11px]">{m.height ? `${m.height} cm` : ""}{m.height && m.weight ? " · " : ""}{m.weight ? `${m.weight} kg` : ""}</p>
                              )}
                              {m.medical_history && <p className="text-[#2E3B57] text-[11px] mt-1"><span className="text-[#A87608] font-semibold">Medical:</span> {m.medical_history}</p>}
                              {(m.hospitalization_reason || m.hospitalization_when) && <p className="text-[#2E3B57] text-[11px] mt-0.5"><span className="text-[#A87608] font-semibold">Past hospitalization:</span> {m.hospitalization_reason || "—"}{m.hospitalization_when ? ` (≈ ${m.hospitalization_when})` : ""}</p>}
                              {m.medication_details && <p className="text-[#2E3B57] text-[11px] mt-0.5"><span className="text-[#A87608] font-semibold">Current medication:</span> {m.medication_details}</p>}
                              {(m.files || []).length > 0 && (
                                <div className="grid sm:grid-cols-2 gap-2 mt-2">{m.files.map((f) => renderFile(f))}</div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Previous policies (portability) */}
                      {(() => {
                        const portFiles = (s.files || []).filter((f) => f.member_key === "portability");
                        if (portFiles.length === 0) return null;
                        return (
                          <div data-testid={`portability-files-${s.id}`}>
                            <p className="text-[#A87608] font-semibold text-xs mb-2 flex items-center gap-1.5"><Repeat className="h-3.5 w-3.5" /> Previous policies (portability)</p>
                            <div className="grid sm:grid-cols-2 gap-2">{portFiles.map((f) => renderFile(f))}</div>
                          </div>
                        );
                      })()}

                      {/* Files not tied to a member (older submissions) */}
                      {(() => {
                        const memberFileIds = new Set((s.members || []).flatMap((m) => (m.files || []).map((f) => f.id)));
                        const orphan = (s.files || []).filter((f) => !memberFileIds.has(f.id) && f.member_key !== "portability");
                        if (orphan.length === 0) return null;
                        return (
                          <div>
                            <p className="text-[#A87608] font-semibold text-xs mb-2">{(s.members || []).length ? "Other documents" : "Uploaded documents"}</p>
                            <div className="grid sm:grid-cols-2 gap-2">{orphan.map((f) => renderFile(f))}</div>
                          </div>
                        );
                      })()}

                      {/* Legacy medical info (older submissions) */}
                      {s.medical_history && <div className="text-xs"><p className="text-[#A87608] font-semibold mb-1">Medical history</p><p className="text-[#2E3B57]">{s.medical_history}</p></div>}
                      {s.hospitalization_details && <div className="text-xs"><p className="text-[#A87608] font-semibold mb-1">Past hospitalization</p><p className="text-[#2E3B57]">{s.hospitalization_details}</p></div>}
                      {s.medication_details && <div className="text-xs"><p className="text-[#A87608] font-semibold mb-1">Current medication</p><p className="text-[#2E3B57]">{s.medication_details}</p></div>}

                      {/* Actions */}
                      <div className="flex items-center gap-3 flex-wrap">
                        <select
                          value={s.status}
                          onChange={(e) => updateStatus(s.id, e.target.value)}
                          className="h-9 px-3 bg-white border border-[#C9D4E6] rounded-xl text-[#1B2A4A] text-xs"
                          data-testid={`status-select-${s.id}`}
                        >
                          <option value="new">New</option>
                          <option value="in_process">In Process</option>
                          <option value="completed">Completed</option>
                        </select>
                        <button
                          onClick={() => openEmailDraft(s)}
                          className="flex items-center gap-1.5 text-xs bg-[#1B2A4A] hover:bg-[#26375c] text-white font-medium px-3 py-2 rounded-lg transition-colors"
                          data-testid={`draft-login-email-${s.id}`}
                        >
                          <Mail className="h-3.5 w-3.5" /> Draft Login Email
                        </button>
                        {isAdvisor() && (
                          <button onClick={() => deleteSub(s.id)}
                            className="flex items-center gap-1 text-xs text-red-600 hover:text-red-700 ml-auto"
                            data-testid={`delete-submission-${s.id}`}>
                            <Trash2 className="h-3.5 w-3.5" /> Delete
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Login Email Draft Modal */}
      {draftSub && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setDraftSub(null); }}
          data-testid="login-email-modal">
          <div className="bg-white w-full sm:max-w-2xl sm:rounded-2xl rounded-t-2xl max-h-[92vh] overflow-y-auto shadow-2xl">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-[#C9D4E6] px-4 sm:px-5 py-3 flex items-center gap-3 z-10">
              <Mail className="h-5 w-5 text-[#A87608]" />
              <div className="flex-1 min-w-0">
                <h3 className="text-[#1B2A4A] font-bold text-sm">Login Email Draft</h3>
                <p className="text-[#3A4557] text-xs truncate">For {draftSub.name} · copy & send from your own email</p>
              </div>
              <button onClick={() => setDraftSub(null)} className="p-2 rounded-lg hover:bg-[#EDF1F8] text-[#3A4557]" data-testid="close-email-modal">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4 sm:p-5 space-y-4">
              {/* Subject */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[#3A4557] text-xs font-semibold">Subject</label>
                  <button onClick={() => copyText(draftSubject, "Subject")} className="text-[#A87608] text-xs flex items-center gap-1 hover:underline" data-testid="copy-subject-btn">
                    <Copy className="h-3 w-3" /> Copy
                  </button>
                </div>
                <input
                  value={draftSubject}
                  onChange={(e) => setDraftSubject(e.target.value)}
                  className="w-full px-3 py-2.5 bg-white border border-[#C9D4E6] rounded-xl text-[#1B2A4A] text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A017]/30"
                  data-testid="draft-subject-input"
                />
              </div>

              {/* Body */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[#3A4557] text-xs font-semibold">Email body</label>
                  <div className="flex items-center gap-3">
                    <button onClick={regenerateDraft} className="text-[#3A4557] text-xs flex items-center gap-1 hover:text-[#1B2A4A]" data-testid="regenerate-draft-btn">
                      <RotateCcw className="h-3 w-3" /> Regenerate
                    </button>
                    <button onClick={() => copyText(draftBody, "Email body")} className="text-[#A87608] text-xs flex items-center gap-1 hover:underline" data-testid="copy-body-btn">
                      <Copy className="h-3 w-3" /> Copy
                    </button>
                  </div>
                </div>
                <textarea
                  value={draftBody}
                  onChange={(e) => setDraftBody(e.target.value)}
                  rows={16}
                  className="w-full px-3 py-2.5 bg-white border border-[#C9D4E6] rounded-xl text-[#1B2A4A] text-[13px] font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#D4A017]/30"
                  data-testid="draft-body-input"
                />
              </div>

              {/* Quote upload */}
              <div className="bg-[#EDF1F8]/40 border border-[#C9D4E6] rounded-xl p-3">
                <p className="text-[#3A4557] text-xs font-semibold mb-2 flex items-center gap-1.5"><Paperclip className="h-3.5 w-3.5" /> Attach a quote (optional, any file)</p>
                {quoteFile ? (
                  <div className="flex items-center gap-2 bg-white border border-[#C9D4E6] rounded-lg px-3 py-2">
                    <FileText className="h-4 w-4 text-[#A87608]" />
                    <span className="text-[#1B2A4A] text-xs truncate flex-1">{quoteFile.name}</span>
                    <button onClick={() => { setQuoteFile(null); regenerateDraft(); }} className="text-[#3A4557] hover:text-red-600" data-testid="remove-quote-btn"><X className="h-4 w-4" /></button>
                  </div>
                ) : (
                  <label className="flex items-center justify-center gap-2 h-10 rounded-lg border border-dashed border-[#C9D4E6] bg-white text-[#2E3B57] text-xs font-medium cursor-pointer hover:border-[#D4A017]/50" data-testid="quote-upload-label">
                    <Paperclip className="h-4 w-4" /> Upload quote
                    <input type="file" className="hidden" onChange={(e) => onQuoteUpload(e.target.files?.[0])} data-testid="quote-upload-input" />
                  </label>
                )}
              </div>

              {/* Attachments summary */}
              <div className="bg-[#EDF1F8]/40 border border-[#C9D4E6] rounded-xl p-3">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-[#3A4557] text-xs font-semibold">Attachments ({(draftSub.files || []).length + (quoteFile ? 1 : 0)})</p>
                  <button onClick={downloadAllAttachments} disabled={dlAll} className="text-[#A87608] text-xs flex items-center gap-1 hover:underline disabled:opacity-50" data-testid="download-all-btn">
                    {dlAll ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />} Download all
                  </button>
                </div>
                <p className="text-[#3A4557] text-[11px]">
                  {(draftSub.files || []).length} client document{(draftSub.files || []).length !== 1 ? "s" : ""}{quoteFile ? " + 1 quote" : ""}. Download them here, then attach in your email.
                </p>
              </div>
            </div>

            {/* Footer actions */}
            <div className="sticky bottom-0 bg-white border-t border-[#C9D4E6] px-4 sm:px-5 py-3 flex flex-col sm:flex-row gap-2">
              <button onClick={() => copyText(`Subject: ${draftSubject}\n\n${draftBody}`, "Full email")}
                className="flex-1 inline-flex items-center justify-center gap-2 h-11 rounded-xl bg-[#D4A017] hover:bg-[#c1930f] text-[#0A0F1A] font-bold text-sm active:scale-95 transition-transform"
                data-testid="copy-full-email-btn">
                <Copy className="h-4 w-4" /> Copy full email
              </button>
              <button onClick={openInMailApp}
                className="flex-1 inline-flex items-center justify-center gap-2 h-11 rounded-xl bg-[#1B2A4A] hover:bg-[#26375c] text-white font-semibold text-sm active:scale-95 transition-transform"
                data-testid="open-mail-app-btn">
                <Mail className="h-4 w-4" /> Open in email app
              </button>
            </div>
          </div>
        </div>
      )}

      <MobileBottomNav onMenuClick={() => setShowSidebar(true)} />
      <MobileSidebar isOpen={showSidebar} onClose={() => setShowSidebar(false)} />
    </div>
  );
}
